import { useState, useEffect } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash, Globe, Lightning, Table, CreditCard, X } from "@phosphor-icons/react";

const EMPTY_PG = { name: "", login_url: "", username: "", password: "", type: "deposit", report_url: "", notes: "" };

export default function SitesPage() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [sheetTesting, setSheetTesting] = useState(null);
  const [sheetResult, setSheetResult] = useState(null);
  const [form, setForm] = useState({
    name: "", code: "", panel_login_url: "", panel_username: "", panel_password: "",
    panel_pin: "", otp_email: "", otp_app_password: "", otp_sender_filter: "",
    google_sheet_id: "", pg_keywords: "", amount_tolerance: 10000, date_tolerance_days: 1, tmp_sheet_id: "",
    tab_config_deposit: "DEPO BANK [PGRaja], E-WALLET",
    tab_config_withdraw: "WD BCA PERMATA, WD MANDIRI BNI BRI",
    tab_config_pulsa: "PULSA",
    tab_config_special: "GANTUNG / PENDING, MISTAKE",
    notes: "",
  });
  const [pgPanels, setPgPanels] = useState([]);
  const [sheetRegistry, setSheetRegistry] = useState([]);

  useEffect(() => { load(); loadSheetRegistry(); }, []);

  const load = async () => {
    try { const { data } = await API.get("/sites"); setSites(data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadSheetRegistry = async () => {
    try { const { data } = await API.get("/sheet-registry"); setSheetRegistry(data); }
    catch (e) { console.error(e); }
  };

  const handleSubmit = async () => {
    try {
      // Build tab_config from form fields
      const buildTabList = (str, layout) => str.split(",").map(s => s.trim()).filter(Boolean).map(name => ({ name, layout }));
      const tab_config = {
        deposit_tabs: buildTabList(form.tab_config_deposit, "depo"),
        withdraw_tabs: buildTabList(form.tab_config_withdraw, "wd"),
        pulsa_tabs: buildTabList(form.tab_config_pulsa, "pulsa"),
        special_tabs: buildTabList(form.tab_config_special, "depo"),
      };

      const payload = {
        ...form,
        pg_keywords: form.pg_keywords ? form.pg_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
        amount_tolerance: parseFloat(form.amount_tolerance) || 10000,
        date_tolerance_days: parseInt(form.date_tolerance_days) || 1,
        pg_panels: pgPanels.filter(pg => pg.name.trim()),
        tab_config,
      };
      // Remove form-only fields from payload
      delete payload.tab_config_deposit;
      delete payload.tab_config_withdraw;
      delete payload.tab_config_pulsa;
      delete payload.tab_config_special;

      if (editing) {
        await API.put(`/sites/${editing}`, payload);
      } else {
        await API.post("/sites", payload);
      }
      setShowForm(false); setEditing(null); resetForm(); load();
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus site ini?")) return;
    try { await API.delete(`/sites/${id}`); load(); } catch { alert("Delete gagal"); }
  };

  const handleTestConnection = async (siteId) => {
    setTesting(siteId); setTestResult(null);
    try {
      const { data } = await API.post(`/sites/${siteId}/test-connection`);
      setTestResult(data);
    } catch (e) { setTestResult({ success: false, error: e.message }); }
    finally { setTesting(null); }
  };

  const handleTestSheet = async (siteId) => {
    setSheetTesting(siteId); setSheetResult(null);
    try {
      const { data } = await API.post(`/sites/${siteId}/test-sheet`);
      setSheetResult({ ...data, siteId });
    } catch (e) { setSheetResult({ success: false, error: e.message, siteId }); }
    finally { setSheetTesting(null); }
  };

  const [clearingSession, setClearingSession] = useState(null);
  const [showClearMenu, setShowClearMenu] = useState(null);
  const handleClearSessions = async (siteId, target = "all") => {
    const labels = { all: "Panel + PG", panel: "Panel saja", pg: "PG saja" };
    if (!window.confirm(`Reset session ${labels[target]} untuk site ini? Login berikutnya akan dari awal.`)) {
      setShowClearMenu(null);
      return;
    }
    setClearingSession(siteId);
    setShowClearMenu(null);
    try {
      const { data } = await API.post(`/sites/${siteId}/clear-sessions?target=${target}`);
      alert(data.message + (data.cleared?.length ? "\n" + data.cleared.join("\n") : ""));
    } catch (e) { alert("Gagal: " + (e.response?.data?.detail || e.message)); }
    finally { setClearingSession(null); }
  };

  const openEdit = (s) => {
    setEditing(s.id);
    const tc = s.tab_config || {};
    const tabStr = (arr, fallback) => (arr || []).map(t => t.name || t).join(", ") || fallback;
    setForm({
      name: s.name, code: s.code, panel_login_url: s.panel_login_url || "",
      panel_username: s.panel_username || "", panel_password: "", panel_pin: "",
      otp_email: s.otp_email || "", otp_app_password: "",
      otp_sender_filter: s.otp_sender_filter || "", google_sheet_id: s.google_sheet_id || "",
      pg_keywords: (s.pg_keywords || []).join(", "),
      amount_tolerance: s.amount_tolerance || 10000, date_tolerance_days: s.date_tolerance_days || 1,
      tmp_sheet_id: s.tmp_sheet_id || "",
      tab_config_deposit: tabStr(tc.deposit_tabs, "DEPO BANK [PGRaja], E-WALLET"),
      tab_config_withdraw: tabStr(tc.withdraw_tabs, "WD BCA PERMATA, WD MANDIRI BNI BRI"),
      tab_config_pulsa: tabStr(tc.pulsa_tabs, "PULSA"),
      tab_config_special: tabStr(tc.special_tabs, "GANTUNG / PENDING, MISTAKE"),
      notes: s.notes || "",
    });
    setPgPanels((s.pg_panels || []).map(pg => ({ ...pg, password: "" })));
    setShowForm(true);
  };

  const resetForm = () => {
    setForm({
      name: "", code: "", panel_login_url: "", panel_username: "", panel_password: "",
      panel_pin: "", otp_email: "", otp_app_password: "", otp_sender_filter: "",
      google_sheet_id: "", pg_keywords: "", amount_tolerance: 10000, date_tolerance_days: 1, tmp_sheet_id: "",
      tab_config_deposit: "DEPO BANK [PGRaja], E-WALLET",
      tab_config_withdraw: "WD BCA PERMATA, WD MANDIRI BNI BRI",
      tab_config_pulsa: "PULSA",
      tab_config_special: "GANTUNG / PENDING, MISTAKE",
      notes: "",
    });
    setPgPanels([]);
  };

  const addPg = () => setPgPanels([...pgPanels, { ...EMPTY_PG }]);
  const removePg = (idx) => setPgPanels(pgPanels.filter((_, i) => i !== idx));
  const updatePg = (idx, field, val) => {
    const updated = [...pgPanels];
    updated[idx] = { ...updated[idx], [field]: val };
    setPgPanels(updated);
  };

  return (
    <div data-testid="sites-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900 font-heading">Sites</h1>
          <p className="text-sm text-zinc-500 mt-1">Kelola website, panel & payment gateway</p>
        </div>
        <Dialog open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) { setEditing(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-site-btn" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm gap-2"><Plus size={16} /> Tambah Site</Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "Tambah"} Site</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-3">
                <FF label="Nama Site" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="site-name" placeholder="PGRaja" />
                <FF label="Kode (unik)" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testId="site-code" placeholder="pgraja" />
              </div>

              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Panel Login</p>
                <div className="space-y-3">
                  <FF label="Login URL" value={form.panel_login_url} onChange={(v) => setForm({ ...form, panel_login_url: v })} testId="site-login-url" placeholder="https://3m3-ns2-admin.com/" />
                  <div className="grid grid-cols-3 gap-3">
                    <FF label="Username" value={form.panel_username} onChange={(v) => setForm({ ...form, panel_username: v })} testId="site-username" />
                    <FF label="Password" value={form.panel_password} onChange={(v) => setForm({ ...form, panel_password: v })} type="password" testId="site-password" placeholder={editing ? "Kosongkan jika tidak diubah" : ""} />
                    <FF label="PIN" value={form.panel_pin} onChange={(v) => setForm({ ...form, panel_pin: v })} type="password" testId="site-pin" placeholder={editing ? "Kosongkan jika tidak diubah" : ""} />
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">OTP / Email (opsional)</p>
                <div className="grid grid-cols-2 gap-3">
                  <FF label="Gmail Address" value={form.otp_email} onChange={(v) => setForm({ ...form, otp_email: v })} testId="site-otp-email" placeholder="finance@gmail.com" />
                  <FF label="App Password" value={form.otp_app_password} onChange={(v) => setForm({ ...form, otp_app_password: v })} type="password" testId="site-otp-password" placeholder={editing ? "Kosongkan jika tidak diubah" : ""} />
                </div>
                <FF label="OTP Sender Filter" value={form.otp_sender_filter} onChange={(v) => setForm({ ...form, otp_sender_filter: v })} testId="site-otp-sender" placeholder="noreply@3mpday.com" />
              </div>

              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Google Sheet</p>
                <FF label="Spreadsheet ID (Rekonsiliasi)" value={form.google_sheet_id} onChange={(v) => setForm({ ...form, google_sheet_id: v })} testId="site-sheet-id" placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms" />
                <div className="mt-2 space-y-1">
                  <Label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">Sheet Tampungan (TMP)</Label>
                  <select data-testid="site-tmp-sheet" value={form.tmp_sheet_id} onChange={e => setForm({...form, tmp_sheet_id: e.target.value})}
                    className="w-full border border-zinc-300 rounded-sm px-2 py-1.5 text-xs h-8 outline-none bg-white">
                    <option value="">-- Tidak ada --</option>
                    {sheetRegistry.filter(s => s.sheet_type === "tmp").map(s => (
                      <option key={s.id} value={s.id}>{s.name} ({s.tab_name || s.spreadsheet_id?.slice(0,12)})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Tab Config (Nama Tab Google Sheet)</p>
                <p className="text-[10px] text-zinc-400 mb-2">Pisahkan nama tab dengan koma. Jika site baru memiliki nama tab berbeda, ubah di sini.</p>
                <div className="grid grid-cols-2 gap-3">
                  <FF label="Deposit Tabs" value={form.tab_config_deposit} onChange={v => setForm({...form, tab_config_deposit: v})} testId="tab-deposit" placeholder="DEPO BANK [PGRaja], E-WALLET" />
                  <FF label="Withdraw Tabs" value={form.tab_config_withdraw} onChange={v => setForm({...form, tab_config_withdraw: v})} testId="tab-withdraw" placeholder="WD BCA PERMATA, WD MANDIRI BNI BRI" />
                  <FF label="Pulsa Tabs" value={form.tab_config_pulsa} onChange={v => setForm({...form, tab_config_pulsa: v})} testId="tab-pulsa" placeholder="PULSA" />
                  <FF label="Special Tabs" value={form.tab_config_special} onChange={v => setForm({...form, tab_config_special: v})} testId="tab-special" placeholder="GANTUNG / PENDING, MISTAKE" />
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-2">Matching Settings</p>
                <div className="grid grid-cols-3 gap-3">
                  <FF label="PG Keywords (koma)" value={form.pg_keywords} onChange={(v) => setForm({ ...form, pg_keywords: v })} testId="site-pg-keywords" placeholder="ZENPAY88, QRISHOKI" />
                  <FF label="Toleransi Nominal (IDR)" value={form.amount_tolerance} onChange={(v) => setForm({ ...form, amount_tolerance: v })} type="number" testId="site-tolerance" />
                  <FF label="Toleransi Tanggal (hari)" value={form.date_tolerance_days} onChange={(v) => setForm({ ...form, date_tolerance_days: v })} type="number" testId="site-date-tolerance" />
                </div>
              </div>

              {/* Payment Gateways Section */}
              <div className="border-t border-zinc-200 pt-3">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Payment Gateways ({pgPanels.length})</p>
                  <Button variant="outline" size="sm" onClick={addPg} className="rounded-sm gap-1 text-xs h-7">
                    <Plus size={12} /> Tambah PG
                  </Button>
                </div>

                {pgPanels.length === 0 ? (
                  <div className="border border-dashed border-zinc-300 rounded-sm p-4 text-center">
                    <CreditCard size={20} className="text-zinc-300 mx-auto mb-1" />
                    <p className="text-xs text-zinc-400">Belum ada PG. Klik "Tambah PG" untuk menambahkan.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pgPanels.map((pg, idx) => (
                      <div key={idx} className="border border-zinc-200 rounded-sm p-3 bg-zinc-50/50 relative">
                        <button onClick={() => removePg(idx)} className="absolute top-2 right-2 text-zinc-400 hover:text-red-500" title="Hapus PG">
                          <X size={14} />
                        </button>
                        <div className="grid grid-cols-3 gap-2 mb-2">
                          <FF label="Nama PG" value={pg.name} onChange={(v) => updatePg(idx, "name", v)} testId={`pg-name-${idx}`} placeholder="QRISHOKI" />
                          <FF label="Login URL" value={pg.login_url} onChange={(v) => updatePg(idx, "login_url", v)} testId={`pg-url-${idx}`} placeholder="https://qrishoki.com/bo/login" />
                          <div className="space-y-1">
                            <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Tipe</Label>
                            <select data-testid={`pg-type-${idx}`} value={pg.type || "deposit"} onChange={(e) => updatePg(idx, "type", e.target.value)}
                              className="w-full border border-zinc-300 rounded-sm px-3 py-2 text-sm outline-none bg-white">
                              <option value="deposit">Deposit</option>
                              <option value="withdraw">Withdraw</option>
                              <option value="both">Deposit & Withdraw</option>
                            </select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <FF label="Username" value={pg.username} onChange={(v) => updatePg(idx, "username", v)} testId={`pg-user-${idx}`} />
                          <FF label="Password" value={pg.password} onChange={(v) => updatePg(idx, "password", v)} type="password" testId={`pg-pass-${idx}`} placeholder={editing ? "Kosongkan jika tidak diubah" : ""} />
                        </div>
                        <div className="grid grid-cols-2 gap-2 mb-2">
                          <FF label="TOTP Secret (Authenticator)" value={pg.totp_secret || ""} onChange={(v) => updatePg(idx, "totp_secret", v)} testId={`pg-totp-${idx}`} placeholder="GOMAHOKTASAKARQ2" />
                          <FF label="Report URL (opsional)" value={pg.report_url || ""} onChange={(v) => updatePg(idx, "report_url", v)} testId={`pg-report-${idx}`} placeholder="https://qrishoki.com/bo/report/deposit" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <FF label="Catatan" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="site-notes" />

              <Button data-testid="save-site-btn" onClick={handleSubmit} className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm">
                {editing ? "Update" : "Buat"} Site
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Test Result */}
      {testResult && (
        <div data-testid="test-result" className={`border rounded-sm p-4 ${testResult.success ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
          <div className="flex justify-between items-start">
            <p data-testid="test-result-status" className={`text-sm font-semibold ${testResult.success ? "text-emerald-700" : "text-red-700"}`}>
              {testResult.success ? "Koneksi Berhasil!" : "Koneksi Gagal"}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setTestResult(null)} className="text-xs h-6 px-2">x</Button>
          </div>
          {testResult.error && <p className="text-xs text-red-600 mt-1">{testResult.error}</p>}
          {testResult.checks && testResult.checks.length > 0 && (
            <div className="mt-2 space-y-1">
              {testResult.checks.map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-xs">
                  <span className={`inline-block w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                    c.status === "ok" ? "bg-emerald-500" : c.status === "warning" ? "bg-amber-500" : c.status === "error" ? "bg-red-500" : "bg-zinc-400"
                  }`} />
                  <span className="font-semibold text-zinc-600 min-w-[80px]">{c.step}:</span>
                  <span className="text-zinc-500 font-mono">{c.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sites List */}
      <div className="space-y-3">
        {loading ? <div className="p-8 text-center text-sm text-zinc-400">Loading...</div> :
         sites.length === 0 ? <div className="border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-400">Belum ada site.</div> :
         sites.map((s) => (
          <div key={s.id} className="border border-zinc-200 rounded-sm p-4 hover:border-zinc-300 transition-colors" data-testid={`site-${s.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-sm bg-zinc-900 flex items-center justify-center text-white text-sm font-bold">
                    {s.code?.slice(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-900">{s.name}</h3>
                    <p className="text-[10px] font-mono text-zinc-400 uppercase tracking-widest">{s.code}</p>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-zinc-400">Panel:</span>{" "}
                    <span className="font-mono text-zinc-600">{s.panel_login_url?.replace("https://", "").slice(0, 25) || "-"}</span>
                  </div>
                  <div>
                    <span className="text-zinc-400">User:</span>{" "}
                    <span className="font-mono text-zinc-600">{s.panel_username || "-"}</span>
                  </div>
                  <div>
                    <span className="text-zinc-400">Sheet:</span>{" "}
                    <span className="font-mono text-zinc-600">{s.google_sheet_id?.slice(0, 15) || "-"}</span>
                  </div>
                  <div>
                    <span className="text-zinc-400">Toleransi:</span>{" "}
                    <span className="font-mono text-zinc-600">Rp {(s.amount_tolerance || 10000).toLocaleString()} / {s.date_tolerance_days || 1}d</span>
                  </div>
                </div>

                {/* PG Panels Info */}
                {s.pg_panels && s.pg_panels.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {s.pg_panels.map((pg, i) => (
                      <span key={i} className="inline-flex items-center gap-1 text-[10px] font-mono bg-violet-50 text-violet-700 px-2 py-0.5 rounded-sm border border-violet-200">
                        <CreditCard size={10} />
                        {pg.name}
                        <span className="text-violet-400">({pg.username || "no user"})</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <Button data-testid={`test-site-${s.id}`} variant="outline" size="sm" onClick={() => handleTestConnection(s.id)}
                  disabled={testing === s.id} className="rounded-sm gap-1 text-xs">
                  <Lightning size={12} /> {testing === s.id ? "..." : "Test Panel"}
                </Button>
                {s.google_sheet_id && (
                  <Button data-testid={`test-sheet-${s.id}`} variant="outline" size="sm" onClick={() => handleTestSheet(s.id)}
                    disabled={sheetTesting === s.id} className="rounded-sm gap-1 text-xs">
                    <Table size={12} /> {sheetTesting === s.id ? "..." : "Test Sheet"}
                  </Button>
                )}
                <div className="relative">
                  <Button data-testid={`clear-session-${s.id}`} variant="outline" size="sm"
                    onClick={() => setShowClearMenu(showClearMenu === s.id ? null : s.id)}
                    disabled={clearingSession === s.id} className="rounded-sm gap-1 text-xs text-amber-600 border-amber-300 hover:bg-amber-50">
                    {clearingSession === s.id ? "..." : "Reset Session"}
                  </Button>
                  {showClearMenu === s.id && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-sm shadow-lg z-20 min-w-[160px]">
                      <button data-testid={`clear-all-${s.id}`} onClick={() => handleClearSessions(s.id, "all")}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 text-zinc-700 border-b border-zinc-100">
                        Reset Semua (Panel + PG)
                      </button>
                      <button data-testid={`clear-panel-${s.id}`} onClick={() => handleClearSessions(s.id, "panel")}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 text-zinc-700 border-b border-zinc-100">
                        Reset Panel Saja
                      </button>
                      <button data-testid={`clear-pg-${s.id}`} onClick={() => handleClearSessions(s.id, "pg")}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 text-zinc-700">
                        Reset PG Saja
                      </button>
                    </div>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="text-zinc-400 hover:text-zinc-900"><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(s.id)} className="text-zinc-400 hover:text-red-600"><Trash size={14} /></Button>
              </div>
            </div>

            {/* Sheet Test Result */}
            {sheetResult && sheetResult.siteId === s.id && (
              <div className={`mt-3 border rounded-sm p-3 ${sheetResult.success ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className={`text-xs font-semibold ${sheetResult.success ? "text-emerald-700" : "text-red-700"}`}>
                      {sheetResult.success ? `Sheet: ${sheetResult.title}` : "Koneksi Sheet Gagal"}
                    </p>
                    {sheetResult.success ? (
                      <div className="mt-1 space-y-1">
                        <p className="text-[10px] text-emerald-600 font-mono">{sheetResult.total_records} total records</p>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {sheetResult.tabs?.map((t, i) => (
                            <span key={i} className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm font-mono">
                              {t.name}: {t.records}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-[10px] text-red-600 mt-1">{sheetResult.error}</p>
                    )}
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setSheetResult(null)} className="text-xs h-6 px-2">x</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FF({ label, value, onChange, type = "text", testId, placeholder = "" }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} className="rounded-sm border-zinc-300" />
    </div>
  );
}
